import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Box, Button, Container, CssBaseline, InputAdornment, TextField, Typography } from "@material-ui/core";
import { AccountCircle } from "@material-ui/icons";
import Lottie from "react-lottie";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import Copyright from "../../components/Copyright";
import ForgotPasswordRedirector from "../../components/ForgotPasswordAnchor";
import LoginLottie from "../../assets/lottie/login.json";
import WhatsappButton from "../../components/WhatsappButton";

import iconLight from "../../assets/miscPng/icon.png";
import iconDark from "../../assets/miscPng/icon-dark.png";
import iconFX from '../../assets/miscPng/fx_sistemas_logo.png';

import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import LottieUtils from "../../utils/classes/LottieUtils";

const useStyles = makeStyles((theme) => ({
  //  ****************
  //  ** Containers **
  //  ****************
  loginContainer: {
    width: "100vw",
    height: "90vh",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    alignItems: "center",
    marginTop: "5vh",
    userSelect: "none",
    name: theme.name,
    color: theme.palette.lottie.plainBackground,
  },

  imgContainer: {
    backgroundColor: theme.palette.lottie.plainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px 0px 0px 10px",
    [theme.breakpoints.down("sm")]: { display: "none", },
  },

  loginForm: {
    backgroundColor: theme.palette.lottie.secondaryPlainBackground,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0px 10px 10px 0px",
  },

  //  ********************
  //  ** Form Container **
  //  ********************
  avatar: {
    margin: theme.spacing(0),
    width: "100px",
    height: "auto",
  },

  form: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },

  textField: {
    width: "80%",

    "& label.MuiFormLabel-root": { color: theme.palette.lottie.plainBackground, },

    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: theme.palette.lottie.plainBackground, },
      "&:hover fieldset": { borderColor: theme.palette.lottie.plainBackground, },
      "&.Mui-focused fieldset": { borderColor: "red", },
    },
  },

  textFieldInput: { color: theme.palette.lottie.plainBackground, },

  floatingButton: {
    margin: theme.spacing(3, 0, 2),
    transition: "transform 0.30s",
    "&:hover": { transform: "translateY(-5px)", },
  },

  secondaryButton: { color: theme.palette.lottie.plainBackground, },

  //  *********
  //  ** Box **
  //  *********
  fxLogo: {
    margin: theme.spacing(1),
    width: "50px",
    height: "auto",
    display: "block",
    marginLeft: "auto",
    marginRight: "auto",
  },

  privacyPolicyButton: {
    display: "inline-block",
    color: theme.palette.secondary.ackCheck,
    transition: "all 0.30s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-5px)",
      textDecoration: "underline",
    },
  },

  boxContainer: {
    display: "flex"
    , flexDirection: "column"
    , alignItems: "center"
    , justifyContent: "center"
  },
}));

const LoginGroup = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const { handleLoginGroup } = useContext(AuthContext);

  const [user, setUser] = useState({ email: "" });

  const currentIcon = localStorage.getItem("theme") ? iconDark : iconLight;

  

  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    user.email = params.get("email") !== "" ? params.get("email") : user.email;
    document.title = "BestZap";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const paramsEmail = new URLSearchParams(location.search).get("email");
    if (paramsEmail !== undefined && paramsEmail !== null && paramsEmail !== "") handleLoginGroup(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleChangeInput = (event) => {
    setUser({ ...user, [event.target.name]: event.target.value });
  };

  const handleSubmit = (event) => {
    event.preventDefault(); handleLoginGroup(user);
  };

  const handlePrivacyPolicyRedirect = () => {
    history.push(DefaultValuesUtils.ROUTES[999]);
  };



  //  ************
  //  ** Return **
  //  ************
  return (
    <Container className={classes.loginContainer} component="main">
      <CssBaseline />

      <div className={classes.imgContainer}>
        <Lottie
          options={{ ...LottieUtils.ANIMATION_PROPERTIES, animationData: LoginLottie }}
          isClickToPauseDisabled={true}
          style={{ width: "80.00%", height: "80.00%", }}
        />
      </div>

      <div className={classes.loginForm}>
        <img className={classes.avatar} src={currentIcon} alt="BestZap Logo" />

        <Typography component="h1" variant="h3">
          {i18n.t("login.title")}
        </Typography>

        <Typography component="h2" variant="h6">
          <br />
          {i18n.t("login.subtitleGroup")}
          <br /><br />
        </Typography>

        <form className={classes.form} noValidate onSubmit={handleSubmit}>
          <TextField
            className={classes.textField}
            label={i18n.t("passwordReset.form.email")}
            autoComplete="on"
            variant="outlined"
            margin="normal"
            id="email"
            name="email"
            type="email"
            value={user.email}
            onChange={handleChangeInput}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><AccountCircle /></InputAdornment>),
              className: classes.textFieldInput
            }}

            autoFocus
            required
          />

          <Button
            type="submit"
            style={{ width: "80%" }}
            variant="contained"
            color="primary"
            className={classes.floatingButton}
          >
            {i18n.t("login.buttons.submit")}
          </Button>

          <ForgotPasswordRedirector />
        </form>

        <Box mt={8} className={classes.boxContainer}>
          <a href="https://www.fxsistemas.com.br" target="_blank" rel="noreferrer">
            <img className={classes.fxLogo} src={iconFX} alt="FX Sistemas Logo" />
          </a>

          {<Copyright />}

          <span className={classes.privacyPolicyButton} onClick={handlePrivacyPolicyRedirect}>
            {i18n.t("mainDrawer.listItems.privacyPolicy")}
          </span>
        </Box>
      </div>

      <WhatsappButton />
    </Container>
  );
};

export default LoginGroup;
